import { useEffect, useState, type ReactNode } from 'react';
import type { CrudFilter } from '@refinedev/core';
import { Select } from 'antd';
import { getFilterValue, type SetFilters } from '@/utils/filters';
import { FilterPanel } from './FilterPanel';
import type { FilterValue } from './SelectFilter';

type MultiSelectFilterProps<T extends FilterValue> = {
  field: string;
  options: { label: ReactNode; value: T }[];
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  placeholder?: string;
  minWidth?: number;
};

/**
 * Multi-choice column filter, applied as `field[in]` (comma-joined by the
 * dataProvider).
 *
 * Writes through `setFilters` rather than antd's own filter state — see
 * `useAdminTable` for why the two must not be mixed.
 */
export const MultiSelectFilter = <T extends FilterValue>({
  field,
  options,
  filters,
  confirm,
  clearFilters,
  setFilters,
  placeholder,
  minWidth = 200,
}: MultiSelectFilterProps<T>) => {
  const current = getFilterValue<T[]>(filters, field, 'in');
  const [pending, setPending] = useState<T[]>(current ?? []);

  useEffect(() => setPending(current ?? []), [current]);

  const handleFilter = () => {
    setFilters(
      [{ field, operator: 'in', value: pending.length ? pending : undefined }],
      'merge'
    );
    confirm();
  };

  const handleReset = () => {
    setPending([]);
    setFilters([{ field, operator: 'in', value: undefined }], 'merge');
    clearFilters?.();
  };

  return (
    <FilterPanel onFilter={handleFilter} onReset={handleReset}>
      <Select
        mode="multiple"
        value={pending}
        onChange={setPending}
        placeholder={placeholder}
        style={{ minWidth }}
        allowClear
        options={options}
      />
    </FilterPanel>
  );
};
